import React from 'react';
import { Activity, Clock, Zap, Heart, Bike, Flame, Dumbbell, Waves, Mountain, TrendingUp, Trophy } from 'lucide-react';
import { PoweredByStrava, ViewOnStravaButton } from './StravaAssets';
import bgRunning from '../assets/bg_running.png';
import bgCycling from '../assets/bg_cycling.png';
import bgIndoorCycling from '../assets/bg_indoor_cycling.png';
import bgGym from '../assets/bg_gym.png';

const UnifiedActivityCard = ({ activity, metrics }) => {
  if (!activity) return null;

  // Lógica de Deporte
  const rawType = (activity.sport_type || activity.type || '').toLowerCase();
  const actName = (activity.name || '').toLowerCase();

  const isIndoorCycling = 
    rawType === 'virtualride' || 
    rawType.includes('indoor') ||
    rawType.includes('spinning') ||
    activity.trainer === true || 
    actName.includes('indoor') || 
    actName.includes('rodillo') || 
    actName.includes('spinning') || 
    actName.includes('zwift') || 
    actName.includes('bkool') ||
    actName.includes('trainer') ||
    actName.includes('ciclo indoor');

  const isOutdoorCycling = !isIndoorCycling && (
    ['ride', 'ebikeride', 'gravelride', 'mountainbikeride', 'ciclismo'].some(t => rawType.includes(t)) ||
    actName.includes('bicicleta') ||
    actName.includes('bici')
  );
  
  const isAnyCycling = isIndoorCycling || isOutdoorCycling;
  const isRunning = ['run', 'virtualrun', 'trailrun', 'carrera'].some(t => rawType.includes(t));
  const isSwimming = rawType.includes('swim') || rawType.includes('natac');
  const isGym = ['weighttraining', 'workout', 'crossfit', 'fuerza', 'pesas', 'gym'].some(t => rawType.includes(t));
  const isRacket = ['tennis', 'padel', 'squash', 'pickleball'].some(t => rawType.includes(t)); 

  let sportColor;
  let sportIcon;
  let bgImage;
  let bgPosition;
  let sportName;

  if (isIndoorCycling) {
    sportColor = '#a855f7'; // Violeta / Púrpura neón Pain Cave
    sportIcon = <Bike size={18} />;
    bgImage = bgIndoorCycling;
    bgPosition = 'right 20% center';
    sportName = 'CICLO INDOOR';
  } else if (isOutdoorCycling) {
    sportColor = '#00e5ff'; // Cyan neón deportivo carretera
    sportIcon = <Bike size={18} />;
    bgImage = bgCycling;
    bgPosition = 'right 15% center';
    sportName = 'CICLISMO';
  } else if (isRunning) {
    sportColor = '#fc4c02'; // Naranja Strava
    sportIcon = <Flame size={18} />;
    bgImage = bgRunning;
    bgPosition = 'right 15% center';
    sportName = 'CARRERA';
  } else if (isSwimming) {
    sportColor = '#06b6d4'; // Cyan
    sportIcon = <Waves size={18} />;
    bgImage = 'https://images.unsplash.com/photo-1519315901367-f34bf9150f5c?fm=jpg&auto=compress&fit=crop&q=80&w=1200';
    bgPosition = 'center center';
    sportName = 'NATACIÓN';
  } else if (isGym) {
    sportColor = '#ec4899'; // Magenta / Rosa neón
    sportIcon = <Dumbbell size={18} />;
    bgImage = bgGym;
    bgPosition = 'right 25% center';
    sportName = 'FUERZA';
  } else if (isRacket) {
    sportColor = '#84cc16'; // Verde lima
    sportIcon = <Activity size={18} />; 
    bgImage = 'https://images.unsplash.com/photo-1599586120429-48281b6f0ece?fm=jpg&auto=compress&fit=crop&q=80&w=1200';
    bgPosition = 'center center';
    sportName = 'RAQUETA';
  } else {
    sportColor = '#f59e0b';
    sportIcon = <Activity size={18} />;
    bgImage = bgRunning;
    bgPosition = 'right 15% center';
    sportName = (activity.sport_type || activity.type || 'ENTRENAMIENTO').toUpperCase();
  }

  // Formatters
  const distanceKm = (activity.distance / 1000).toFixed(2);
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const durationFormatted = formatTime(activity.moving_time);
  
  const getPaceOrSpeed = () => {
    if (isAnyCycling) {
      return { value: (activity.average_speed * 3.6).toFixed(1), unit: 'km/h' };
    } else {
      if (!activity.average_speed || activity.average_speed === 0) return { value: '-', unit: 'min/km' };
      const paceSecs = 1000 / activity.average_speed;
      const paceMins = Math.floor(paceSecs / 60);
      const paceRemSecs = Math.floor(paceSecs % 60);
      return { value: `${paceMins}:${paceRemSecs.toString().padStart(2, '0')}`, unit: 'min/km' };
    }
  };
  const pace = getPaceOrSpeed();

  const elevationGain = Math.round(activity.total_elevation_gain || activity.elevation_gain || 0);

  return (
    <div style={{
      position: 'relative',
      borderRadius: '20px',
      overflow: 'hidden',
      color: 'white',
      boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderTop: `2px solid ${sportColor}`,
      background: '#0b1329',
      minHeight: '220px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }}>
      {/* 1. Capa de Fotografía Cinematográfica (Posicionada al 80% derecha, nítida y vibrante) */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        backgroundImage: typeof bgImage === 'string' && bgImage.startsWith('http') ? `url("${bgImage}")` : `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: bgPosition,
        opacity: 0.88,
        zIndex: 0,
        transform: 'scale(1.01)',
        transition: 'all 0.4s ease'
      }} />

      {/* 2. Capa Split Gradient Horizontal (100% contraste a la izquierda, transparencia cinematográfica a la derecha) */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to right, #0b1329 0%, #0b1329 34%, rgba(11, 19, 41, 0.88) 54%, rgba(11, 19, 41, 0.35) 80%, rgba(11, 19, 41, 0.55) 100%)',
        zIndex: 1
      }} />

      {/* 3. Viñeta vertical suave para fundir bordes superior e inferior */}
      <div style={{
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, rgba(11, 19, 41, 0.45) 0%, transparent 25%, transparent 70%, rgba(11, 19, 41, 0.85) 100%)',
        zIndex: 1,
        pointerEvents: 'none'
      }} />

      {/* 4. Resplandor sutil con el color del deporte (sin mix-blend-mode para compatibilidad iOS) */}
      <div style={{
        position: 'absolute',
        top: '-40px',
        left: '-40px',
        width: '160px',
        height: '160px',
        background: sportColor,
        filter: 'blur(70px)',
        opacity: 0.18,
        borderRadius: '50%',
        zIndex: 1,
        pointerEvents: 'none'
      }} />

      {/* 5. Contenido Interactivo y Métricas */}
      <div style={{ position: 'relative', zIndex: 2, padding: '1.4rem 1.6rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', boxSizing: 'border-box' }}>
        
        {/* Header de la Actividad */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
          <div style={{ minWidth: 0, maxWidth: '78%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '3px 10px',
                borderRadius: '20px',
                background: `${sportColor}22`,
                border: `1px solid ${sportColor}55`,
                color: sportColor,
                fontSize: '0.74rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                backdropFilter: 'blur(8px)'
              }}>
                {sportIcon}
                {sportName}
              </span>

              {activity.workout_type === 3 && (
                <span style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  color: '#fca5a5'
                }}>
                  Competición
                </span>
              )}
            </div>

            <h3 style={{
              margin: 0,
              fontSize: '1.35rem',
              fontWeight: 800,
              lineHeight: 1.25,
              color: 'white',
              textShadow: '0 2px 10px rgba(0,0,0,0.7)',
              wordWrap: 'break-word',
              overflowWrap: 'break-word'
            }}>
              {activity.name}
            </h3>
          </div>
        </div>

        {/* Dashboard de Métricas con Alta Legibilidad */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: activity.distance > 0 ? 'auto 1fr' : '1fr',
          gap: '1.5rem',
          alignItems: 'center'
        }}>
          {/* Métrica Primaria Gigante (Distancia) */}
          {activity.distance > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                <span style={{
                  fontSize: '3.4rem',
                  fontWeight: 900,
                  lineHeight: 0.95,
                  letterSpacing: '-0.04em',
                  color: 'white',
                  textShadow: '0 2px 14px rgba(0,0,0,0.6)'
                }}>
                  {distanceKm}
                </span>
              </div>
              <span style={{
                fontSize: '0.78rem',
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginTop: '6px'
              }}>
                Kilómetros
              </span>
            </div>
          )}

          {/* Métricas Secundarias en Grid Compacto (Glassmorphism sutil) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
            gap: '0.75rem',
            background: 'rgba(11, 19, 41, 0.65)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            padding: '0.75rem 1rem',
            borderRadius: '14px',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
          }}>
            {/* Tiempo */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Clock size={11} color="rgba(255,255,255,0.8)" /> Tiempo
              </span>
              <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
                {durationFormatted}
              </span>
            </div>

            {/* Ritmo / Velocidad */}
            {activity.distance > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                  <Zap size={11} color={sportColor} /> {isAnyCycling ? 'Vel. Med' : 'Ritmo'}
                </span>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
                  {pace.value} <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>{pace.unit}</span>
                </span>
              </div>
            )}

            {/* FC Media */}
            {activity.has_heartrate && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                  <Heart size={11} color="#f43f5e" /> FC Media
                </span>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
                  {Math.round(activity.average_heartrate)} <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>ppm</span>
                </span>
              </div>
            )}

            {/* Desnivel Positivo si existe */}
            {elevationGain > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
                  <Mountain size={11} color="#38bdf8" /> Desnivel
                </span>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: 'white', letterSpacing: '-0.02em' }}>
                  +{elevationGain} <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600 }}>m</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* FILA DE 4 TARJETAS FISIOLÓGICAS INTEGRADAS DENTRO DEL HERO */}
        {metrics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
            gap: '0.65rem',
            marginTop: '1.15rem'
          }}>
            {/* TSS / Carga Aguda */}
            <div style={{
              background: 'rgba(11, 19, 41, 0.72)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '0.65rem 0.85rem',
              boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
            }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <TrendingUp size={12} style={{ color: 'var(--strava-orange, #fc4c02)' }} /> Carga / TSS
              </span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '3px' }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--strava-orange, #fc4c02)' }}>
                  {metrics.tss || activity.tss || metrics.sufferScore || '--'}
                </span>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>TSS</span>
              </div>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', display: 'block' }}>Aporte a fatiga aguda</span>
            </div>

            {/* NP o Eficiencia Aeróbica */}
            {(activity.normalizedPower > 0 || metrics.aerobicEfficiency) && (
              <div style={{
                background: 'rgba(11, 19, 41, 0.72)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '0.65rem 0.85rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
              }}>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Zap size={12} style={{ color: '#f59e0b' }} /> {activity.normalizedPower > 0 ? 'Potencia Norm.' : 'Eficiencia Aeróbica'}
                </span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '3px' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#f59e0b' }}>
                    {activity.normalizedPower || metrics.aerobicEfficiency}
                  </span>
                  <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>
                    {activity.normalizedPower ? 'W' : (isAnyCycling ? 'km/h/ppm' : 'm/min/ppm')}
                  </span>
                </div>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                  {activity.intensityFactor ? `IF: ${activity.intensityFactor}` : 'Coste fisiológico real'}
                </span>
              </div>
            )}

            {/* Carga Mecánica Articular (Running) o Desacoplamiento (Ciclismo) */}
            {(activity.mechanicalLoad > 0 || isRunning || (metrics.cardiacDrift !== null && metrics.cardiacDrift !== undefined)) && (
              <div style={{
                background: 'rgba(11, 19, 41, 0.72)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '0.65rem 0.85rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
              }}>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Activity size={12} style={{ color: isRunning ? '#38bdf8' : (metrics.driftStatus?.color || '#38bdf8') }} />
                  {isRunning ? 'Carga Mecánica' : 'Cardiac Drift'}
                </span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', marginTop: '3px' }}>
                  <span style={{ fontSize: '1.25rem', fontWeight: 800, color: isRunning ? '#38bdf8' : (metrics.driftStatus?.color || '#38bdf8') }}>
                    {isRunning ? (activity.mechanicalLoad || Math.round((activity.distance / 1000) * 1.8)) : `${metrics.cardiacDrift}%`}
                  </span>
                  <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>
                    {isRunning ? 'pts impacto' : 'deriva'}
                  </span>
                </div>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                  {isRunning ? 'Estrés articular' : (metrics.driftStatus?.text || 'Estabilidad aeróbica')}
                </span>
              </div>
            )}

            {/* Training Effect Badge */}
            {metrics.effect && (
              <div style={{
                background: 'rgba(11, 19, 41, 0.72)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: `1px solid ${metrics.effect.color}45`,
                borderRadius: '12px',
                padding: '0.65rem 0.85rem',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
              }}>
                <span style={{ fontSize: '0.68rem', color: metrics.effect.color, fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Trophy size={12} /> Training Effect
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '3px' }}>
                  <span style={{ fontSize: '0.94rem', fontWeight: 800, color: metrics.effect.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {metrics.effect.label}
                  </span>
                </div>
                <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {metrics.effect.desc}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Footer con Powered by Strava y Enlace Reglamentario a Strava */}
        {(!activity.source || activity.source === 'strava') && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1.15rem',
            paddingTop: '0.65rem',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            flexWrap: 'wrap',
            gap: '0.6rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <PoweredByStrava light={true} />
              <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                · Telemetría 1Hz
              </span>
            </div>
            <ViewOnStravaButton activityId={activity.id} compact={true} />
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedActivityCard;
